import { Tooltip } from '@mantine/core'
import React, { useEffect, useRef, useState } from 'react'

type EllipsisTooltipProps = {
  fullText: string
  maxWidth?: number
}

export const EllipsisTooltip: React.FC<EllipsisTooltipProps> = ({ fullText, maxWidth = 350 }) => {
  const textRef = useRef<HTMLDivElement | null>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)

  const checkOverflow = () => {
    const el = textRef.current

    if (!el) return

    // Double requestAnimationFrame for guaranteed layout
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        const overflow = el.scrollWidth > el.offsetWidth

        setIsOverflowing(overflow)
      })
    })
  }

  useEffect(() => {
    const el = textRef.current

    if (!el) return

    const timeout = setTimeout(checkOverflow, 100)
    const resizeObserver = new ResizeObserver(checkOverflow)

    resizeObserver.observe(el)

    const mutationObserver = new MutationObserver(checkOverflow)

    mutationObserver.observe(el, {
      characterData: true,
      childList: true,
      subtree: true,
    })

    return () => {
      clearTimeout(timeout)

      resizeObserver.disconnect()

      mutationObserver.disconnect()
    }
  }, [fullText])

  return (
    <Tooltip
      label={fullText}
      position="right"
      withinPortal
      withArrow
      disabled={!isOverflowing}
      styles={{
        tooltip: {
          position: 'fixed',
          zIndex: 2000,
          maxWidth: 'none',
          whiteSpace: 'nowrap',
          backgroundColor: 'rgba(0, 0, 0, 0.9)',
          padding: '8px 12px',
          fontSize: '14px',
        },
      }}
    >
      <div
        ref={textRef}
        style={{
          maxWidth: `${maxWidth}px`,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
          display: 'block',
          cursor: isOverflowing ? 'pointer' : 'default',
        }}
      >
        {fullText}
      </div>
    </Tooltip>
  )
}
